import { Link } from "react-router-dom";
import { ChevronRight, BookOpen, Truck, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import useSEO from "@/hooks/useSEO";
import { useTrackPageView } from "@/lib/analytics";
import KineticTitle, { HeroFade } from "@/components/books/KineticTitle";
import Reveal from "@/components/books/Reveal";
import MarqueeRibbon from "@/components/books/MarqueeRibbon";
import BookCover from "@/components/books/BookCover";
import PhysicalStickyBar from "@/components/books/PhysicalStickyBar";
import BilingualBadge from "@/components/books/BilingualBadge";
import { CHINNU_BOOKS, CHINNU_BUNDLE, CHINNU_AUTHOR } from "@/data/books";

export default function ChinnuBundle() {
  useTrackPageView("bundle_view", { product_id: CHINNU_BUNDLE.print_product_id, source: "bundle-page" });
  useSEO({
    title: "Complete Chinnu Talking Adventures Collection — Printed Books · LifeIQ Books",
    description: "All four bilingual English–Telugu Chinnu Talking Adventures printed books, shipped together across India. The complete journey from everyday chatter to quiet confidence.",
    canonicalPath: "/books/chinnu/complete-collection",
  });

  return (
    <main>
      {/* BUNDLE HERO — fanned covers + price strike */}
      <section className="relative bg-[#0D1B2A] text-white overflow-hidden">
        <span
          aria-hidden="true"
          className="font-telugu absolute -top-10 right-0 text-[20vw] leading-none text-outline-cream opacity-40 pointer-events-none select-none"
        >
          పూర్తి
        </span>
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-16 md:pt-24 pb-20 md:pb-28 relative">
          <HeroFade delay={0}>
            <nav className="flex items-center gap-2 text-[13px] text-white/50 flex-wrap">
              <Link to="/books" className="hover:text-white transition-colors">LifeIQ Books</Link>
              <ChevronRight size={14} />
              <Link to="/books/chinnu" className="hover:text-white transition-colors">Chinnu Talking Adventures</Link>
              <ChevronRight size={14} />
              <span className="text-white font-medium">Complete Collection</span>
            </nav>
          </HeroFade>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-7">
              <HeroFade delay={0.05}>
                <div className="text-[12px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">The complete collection</div>
              </HeroFade>
              <KineticTitle
                testId="bundle-hero-title"
                className="mt-5 font-serif-d text-[38px] sm:text-5xl lg:text-[58px] leading-[1.05] tracking-tight text-white"
                lines={["The Complete", "Chinnu Collection"]}
              />
              <HeroFade delay={0.4}>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <BilingualBadge dark testId="bundle-bilingual-badge" />
                  <p className="text-[13px] uppercase tracking-[0.2em] text-white/70 font-semibold">4 Interactive Bilingual English–Telugu Books</p>
                </div>
                <p className="mt-3 font-telugu text-[20px] sm:text-[22px] text-[#F5B25C]">{CHINNU_BUNDLE.title_telugu}</p>
                <p className="mt-5 text-[16px] sm:text-[17px] text-white/85 leading-[1.7] max-w-xl">{CHINNU_BUNDLE.description}</p>
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-white/65">
                  <span>Languages: {CHINNU_BUNDLE.languages}</span>
                  <span>Printed Book · 4 titles</span>
                </div>

                <div data-testid="bundle-price-badge" className="mt-8">
                  <div className="mt-2 flex flex-wrap items-end gap-x-4 gap-y-2">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-semibold">Complete 4-Book Printed Collection</div>
                      <div className="mt-1 font-serif-d text-[52px] sm:text-[60px] leading-none text-[#F57F17]">{CHINNU_BUNDLE.print_price}</div>
                    </div>
                    <div className="pb-2 text-[12px] uppercase tracking-[0.14em] font-bold text-[#0D1B2A] bg-[#F5B25C] px-2.5 py-1 rounded-sm">
                      Save {CHINNU_BUNDLE.print_savings}
                    </div>
                  </div>
                </div>

                <p data-testid="bundle-buy-context" className="mt-6 text-[12px] uppercase tracking-[0.14em] text-white/60 font-semibold">Bilingual English–Telugu · Printed Book</p>
                <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-start">
                  <Link
                    to="/books/chinnu/checkout/complete-collection"
                    data-testid="bundle-primary-buy-button"
                    className="inline-flex items-center justify-center gap-2 px-7 py-4 text-[13px] font-semibold tracking-wide uppercase text-white hover:opacity-90 transition-opacity duration-200 rounded-sm"
                    style={{ backgroundColor: "#F57F17" }}
                  >
                    Get All 4 Printed Books — {CHINNU_BUNDLE.print_price}
                  </Link>
                  <a
                    href="#included"
                    data-testid="bundle-see-included"
                    className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-white/10 transition-colors duration-200 rounded-sm"
                  >
                    What's Included
                  </a>
                </div>
                <p className="mt-3 text-[13px] text-white/55">Printed-book delivery is currently available within India only.</p>
              </HeroFade>
            </div>

            {/* Fan of covers */}
            <div className="lg:col-span-5">
              <HeroFade delay={0.35} className="relative h-[380px] sm:h-[440px] hidden sm:block">
                {CHINNU_BOOKS.map((b, i) => (
                  <motion.div
                    key={b.slug}
                    className="absolute w-[42%] left-1/2 top-1/2 origin-bottom"
                    style={{ x: "-50%", y: "-50%" }}
                    initial={{ opacity: 0, rotate: 0 }}
                    animate={{ opacity: 1, rotate: (i - 1.5) * 9, x: `calc(-50% + ${(i - 1.5) * 34}px)`, y: `calc(-50% + ${Math.abs(i - 1.5) * 14}px)` }}
                    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.12 }}
                  >
                    <BookCover book={b} testId={`bundle-cover-${b.slug}`} shadow="book-shadow-3d" />
                  </motion.div>
                ))}
              </HeroFade>
              <div className="grid grid-cols-4 gap-2.5 sm:hidden">
                {CHINNU_BOOKS.map((b) => <BookCover key={b.slug} book={b} />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarqueeRibbon tone="cream" />

      {/* INCLUDED BOOKS */}
      <section id="included" className="bg-white">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">Inside the collection</div>
            <h2 className="mt-5 font-serif-d text-[32px] sm:text-4xl lg:text-[44px] leading-[1.08] text-[#0D1B2A]">All four adventures, together.</h2>
          </Reveal>
          <div className="mt-14 space-y-5">
            {CHINNU_BOOKS.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.06}>
                <Link
                  to={`/books/chinnu/${b.slug}`}
                  data-testid={`bundle-item-book-${b.book_number}`}
                  className="group grid grid-cols-12 gap-5 items-center bg-[#FBF7EE] border border-[#E7DFCF] rounded-sm p-5 sm:p-6 hover:border-[#F57F17]/60 hover:shadow-[0_8px_30px_rgba(13,27,42,0.08)] transition-all duration-200"
                >
                  <div className="col-span-3 sm:col-span-2 max-w-[110px]"><BookCover book={b} /></div>
                  <div className="col-span-9 sm:col-span-7">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">Book {b.book_number}</div>
                    <div className="mt-1 font-serif-d text-[21px] sm:text-[23px] text-[#0D1B2A] leading-tight">
                      {b.title} <span className="font-telugu text-[0.72em] text-[#D86B0F] ml-1.5">{b.title_telugu}</span>
                    </div>
                    <div className="mt-1.5"><BilingualBadge compact testId={`bundle-item-bilingual-badge-${b.slug}`} /></div>
                    <p className="mt-1.5 text-[14px] text-[#5C6470] leading-[1.6] line-clamp-2">{b.short_description}</p>
                  </div>
                  <div className="col-span-12 sm:col-span-3 flex sm:flex-col items-center sm:items-end justify-between gap-2">
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif-d text-[20px] text-[#0D1B2A]">{b.print_price}</span>
                    </div>
                    <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-[#004D40] group-hover:underline">View book →</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-5 bg-[#0D1B2A] text-white rounded-sm p-6 sm:p-8">
              <div className="text-center sm:text-left">
                <div className="font-serif-d text-[22px]">Four printed books. {CHINNU_BUNDLE.print_price}. One quiet, confident voice.</div>
                <div className="mt-1 text-[13px] text-white/60">Worth ₹1,046 bought separately — you save {CHINNU_BUNDLE.print_savings}.</div>
                <p data-testid="bundle-included-buy-context" className="mt-2 text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold">Bilingual English–Telugu · Printed Book</p>
              </div>
              <Link
                to="/books/chinnu/checkout/complete-collection"
                data-testid="bundle-secondary-buy-button"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 text-[13px] font-semibold tracking-wide uppercase text-white hover:opacity-90 transition-opacity duration-200 rounded-sm"
                style={{ backgroundColor: "#F57F17" }}
              >
                Get All 4 Printed Books — {CHINNU_BUNDLE.print_price}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRINTED BOOK FACTS */}
      <section className="bg-[#FBF7EE]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div data-testid="bundle-printed-book-facts" className="mb-12 grid grid-cols-2 sm:grid-cols-2 gap-6 max-w-md">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C6470] font-semibold">Languages</div>
                <div className="mt-2 font-serif-d text-[17px] text-[#0D1B2A] leading-snug">{CHINNU_BUNDLE.languages}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C6470] font-semibold">Format</div>
                <div className="mt-2 font-serif-d text-[17px] text-[#0D1B2A] leading-snug">Printed Book · 4 titles</div>
              </div>
            </div>
            <div data-testid="delivery-guarantee-box" className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {[
                { Icon: BookOpen, t: "Full-colour printed books", d: "Child-friendly paperback printing of all four Chinnu adventures — yours to keep on the shelf." },
                { Icon: Truck, t: "Shipped across India", d: "Dispatched after payment is confirmed, straight to the delivery address you provide at checkout." },
                { Icon: ShieldCheck, t: "Support if something's wrong", d: "Order arrived damaged or incomplete? Write to us with your order reference and we'll sort it out." },
              ].map(({ Icon, t, d }) => (
                <div key={t} className="text-center md:text-left">
                  <div className="w-11 h-11 rounded-full bg-[#004D40] text-white flex items-center justify-center mx-auto md:mx-0"><Icon size={19} /></div>
                  <div className="mt-4 font-serif-d text-[19px] text-[#0D1B2A]">{t}</div>
                  <p className="mt-2 text-[14px] text-[#5C6470] leading-[1.65]">{d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* AUTHOR — small, optional mention */}
      <section className="bg-white border-t border-[#E7DFCF]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-14">
          <Reveal>
            <div data-testid="bundle-author-block" className="flex items-center gap-4">
              <img
                src={CHINNU_AUTHOR.photo}
                alt={CHINNU_AUTHOR.photo_alt}
                className="w-12 h-12 rounded-full object-cover object-top shrink-0 border border-[#E7DFCF]"
              />
              <div className="text-[13px] text-[#5C6470]">
                Written & designed by <span className="font-semibold text-[#0D1B2A]">{CHINNU_AUTHOR.name}</span> — {CHINNU_AUTHOR.role}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MOBILE STICKY BUY BAR */}
      <PhysicalStickyBar
        href="/books/chinnu/checkout/complete-collection"
        price={CHINNU_BUNDLE.print_price}
        label="Get All 4"
        testId="bundle-sticky-buy"
      />
    </main>
  );
}
